import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { uploadMedia } from "../../services/mediaLibraryService";
import CropperModal from "./CropperModal";
import MediaPreview from "./MediaPreview";

const ACCEPTED = [".jpg", ".jpeg", ".png", ".webp"];
const ACCEPT_ATTR = ACCEPTED.join(",");
const MAX_SIZE_MB = 5;

const extractUploaded = (res) => {
  const data = res?.data;
  return data?.result ?? data?.Result ?? data?.data ?? data ?? null;
};

const nullSafeText = (value) => (value == null ? "" : String(value));

const normalizeMedia = (raw) => {
  if (!raw) return null;
  return {
    mediaAssetId:
      raw.mediaAssetId ?? raw.id ?? raw._id ?? raw.mediaId ?? raw.assetId,
    mediaUrl: raw.mediaUrl ?? raw.url ?? raw.fileUrl ?? raw.path ?? "",
    thumbnailUrl:
      raw.thumbnailUrl ??
      raw.thumbUrl ??
      raw.thumbnail ??
      raw.mediaUrl ??
      raw.url ??
      "",
    mediaName:
      raw.mediaName ?? raw.name ?? raw.fileName ?? raw.title ?? "Untitled",
    providerType: raw.providerType ?? raw.provider ?? raw.source ?? "Upload",
    width: raw.width ?? raw.imageWidth ?? raw.dimensions?.width ?? null,
    height: raw.height ?? raw.imageHeight ?? raw.dimensions?.height ?? null,
    fileSize: raw.fileSize ?? raw.size ?? raw.sizeInBytes ?? raw.bytes ?? null,
    altText: nullSafeText(raw.altText ?? raw.alt_text ?? raw.alt),
    caption: nullSafeText(raw.caption),
    tags: Array.isArray(raw.tags)
      ? raw.tags.join(", ")
      : nullSafeText(raw.tags),
    description: nullSafeText(raw.description),
  };
};

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!value || Number.isNaN(value)) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

const formatImageFormat = (fileType, fileName) => {
  if (fileType) {
    const subtype = fileType.split("/")[1]?.toLowerCase();
    if (subtype === "jpeg" || subtype === "jpg") return "JPEG";
    if (subtype === "png") return "PNG";
    if (subtype === "webp") return "WEBP";
    if (subtype) return subtype.toUpperCase();
  }
  const ext = fileName?.split(".").pop()?.toUpperCase();
  return ext || "—";
};

/**
 * Upload Device tab — drag & drop / browse → crop → preview → upload.
 * Notifies the parent via onUploaded so it can refresh + highlight the asset.
 */
const UploadDeviceTab = ({ onUploaded }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [sourceFile, setSourceFile] = useState(null);
  const [croppedFile, setCroppedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [cropMeta, setCropMeta] = useState(null);
  const [originalName, setOriginalName] = useState("image.png");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  const validate = (file) => {
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED.includes(ext)) {
      toast.error("Unsupported format. Use JPG, JPEG, PNG or WEBP.");
      return false;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${MAX_SIZE_MB} MB.`);
      return false;
    }
    return true;
  };

  const handleFile = useCallback((file) => {
    if (!file || !validate(file)) return;
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setOriginalName(file.name);
    setSourceFile(file);
    setCropSrc(URL.createObjectURL(file));
    setShowCropper(true);
  }, [cropSrc]);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleCropComplete = (file, preview, meta) => {
    setCroppedFile(file);
    setPreviewUrl(preview);
    setCropMeta(
      meta || {
        width: null,
        height: null,
        format: formatImageFormat(file?.type, file?.name),
        estimatedSize: file?.size,
      }
    );
    setShowCropper(false);
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc("");
    setSourceFile(null);
  };

  const resetState = () => {
    setCroppedFile(null);
    setPreviewUrl("");
    setCropMeta(null);
    setCropSrc("");
    setSourceFile(null);
  };

  const handleUpload = async () => {
    if (!croppedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("MediaFile", croppedFile, originalName);
      const res = await uploadMedia(formData);
      const uploaded = normalizeMedia(extractUploaded(res));
      toast.success("Image uploaded successfully.");
      resetState();
      onUploaded?.(uploaded);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (uploading) {
    return (
      <div className="xc-ml-upload-state">
        <div className="xc-ml-upload-state__spinner" />
        <span className="xc-ml-dropzone__title">Uploading image...</span>
        <span className="xc-ml-dropzone__hint">Please wait a moment</span>
      </div>
    );
  }

  if (croppedFile && previewUrl) {
    const previewWidth = cropMeta?.width;
    const previewHeight = cropMeta?.height;
    const previewFormat =
      cropMeta?.format || formatImageFormat(croppedFile.type, croppedFile.name);
    const previewSize = formatBytes(
      cropMeta?.estimatedSize ?? croppedFile.size
    );
    const previewDimensions =
      previewWidth && previewHeight
        ? `${previewWidth} × ${previewHeight} px`
        : "—";

    return (
      <div className="xc-ml-upload-preview">
        <div
          className="xc-ml-preview"
          style={{ maxHeight: "50vh", borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff" }}
        >
          <MediaPreview src={previewUrl} alt="Cropped preview" />
        </div>

        <div className="xc-ml-cropper__panel xc-ml-cropper__panel--preview mt-3">
          <p className="xc-ml-cropper__panel-title">Cropped Output</p>
          <p className="xc-ml-cropper__panel-value">{previewDimensions}</p>
          <div className="xc-ml-cropper__preview-meta">
            <span className="xc-ml-cropper__format">{previewFormat}</span>
            <span className="xc-ml-cropper__panel-meta">{previewSize}</span>
          </div>
        </div>

        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-primary" onClick={handleUpload}>
            <i className="ri-upload-cloud-2-line me-1" aria-hidden="true" />
            Upload
          </button>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => {
              resetState();
              inputRef.current?.click();
            }}
          >
            <i className="ri-refresh-line me-1" aria-hidden="true" />
            Choose Different
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={resetState}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="xc-ml-upload-input"
        onChange={handleInputChange}
      />

      <div
        role="button"
        tabIndex={0}
        className={`xc-ml-dropzone ${isDragging ? "is-dragging" : ""}`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!isDragging) setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
      >
        <span className="xc-ml-dropzone__icon">
          <i className="ri-upload-cloud-2-line" aria-hidden="true" />
        </span>
        <span className="xc-ml-dropzone__title">Drag &amp; Drop image here</span>
        <span className="xc-ml-dropzone__or">or</span>
        <span className="xc-ml-dropzone__browse">Browse Files</span>
        <span className="xc-ml-dropzone__hint">
          Supported: <strong>JPG, JPEG, PNG, WEBP</strong>
          <br />
          Recommended: <strong>Max {MAX_SIZE_MB} MB</strong>
        </span>
      </div>

      <CropperModal
        show={showCropper}
        image={cropSrc}
        fileName={originalName}
        sourceFile={sourceFile}
        onCancel={handleCancelCrop}
        onCropComplete={handleCropComplete}
      />
    </>
  );
};

export default UploadDeviceTab;
